// frontend/src/components/admin/CronManager.jsx
import { useState, useEffect, useCallback } from 'react';
import {
  Clock, Play, Calendar, Power, RefreshCw,
  CheckCircle2, XCircle, AlertCircle, ChevronDown,
  ChevronUp, Zap, Ban, Info
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import superAdminApi from '../../services/superAdminApi';

// ============================================================================
// HELPERS
// ============================================================================

const formatDateTime = (dt) => {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const formatDuration = (start, end) => {
  if (!start || !end) return '—';
  const ms = new Date(end) - new Date(start);
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const StatusBadge = ({ status, theme }) => {
  const config = {
    success: { icon: CheckCircle2, color: theme.successText, bg: theme.successSoft, label: 'Succès' },
    error:   { icon: XCircle,      color: theme.dangerText,  bg: theme.dangerSoft,  label: 'Erreur' },
    running: { icon: RefreshCw,    color: theme.primary,     bg: theme.primarySoft, label: 'En cours' },
  }[status] || { icon: AlertCircle, color: theme.textMuted, bg: theme.bgInput, label: status };

  return (
    <span className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-full font-medium"
      style={{ background: config.bg, color: config.color }}>
      <config.icon size={11} className={status === 'running' ? 'animate-spin' : ''} />
      {config.label}
    </span>
  );
};

// ============================================================================
// COMPOSANT LOG ROW
// ============================================================================

const LogRow = ({ log, theme }) => {
  const [expanded, setExpanded] = useState(false);
  let details = [];
  try { details = JSON.parse(log.details || '[]'); } catch {}

  return (
    <div className="rounded-xl overflow-hidden mb-2" style={{ border: `1px solid ${theme.border}` }}>
      <div
        className="flex items-center gap-4 p-3 cursor-pointer"
        onClick={() => setExpanded(e => !e)}
        style={{ background: theme.bgCard }}
      >
        <StatusBadge status={log.status} theme={theme} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium" style={{ color: theme.text }}>
              {formatDateTime(log.started_at)}
            </span>
            <span className="text-xs px-2 py-0.5 rounded-md"
              style={{ background: theme.bgInput, color: theme.textMuted }}>
              {log.triggered_by === 'manual' ? '🖱️ Manuel' : log.triggered_by === 'scheduled' ? '📅 Programmé' : '⏰ Auto'}
            </span>
          </div>
          <p className="text-xs mt-0.5" style={{ color: theme.textMuted }}>
            {log.reminders_sent} envoyé(s) · {log.reminders_skipped} ignoré(s) · durée {formatDuration(log.started_at, log.ended_at)}
          </p>
        </div>
        {details.length > 0 && (
          expanded ? <ChevronUp size={14} style={{ color: theme.textMuted }} /> : <ChevronDown size={14} style={{ color: theme.textMuted }} />
        )}
      </div>

      {expanded && details.length > 0 && (
        <div className="p-3 border-t space-y-1" style={{ borderColor: theme.border, background: theme.bgInput }}>
          {details.map((d, i) => (
            <div key={i} className="flex items-center gap-3 text-xs">
              <span style={{ color: d.status === 'sent' ? theme.successText : theme.textMuted }}>
                {d.status === 'sent' ? '✅' : '⏭️'}
              </span>
              <span style={{ color: theme.text }}>{d.company_name}</span>
              <span style={{ color: theme.textMuted }}>J-{d.days_before}</span>
              {d.echeance && <span style={{ color: theme.textMuted }}>→ {d.echeance}</span>}
            </div>
          ))}
          {log.error_message && (
            <p className="text-xs mt-2" style={{ color: theme.dangerText }}>
              Erreur : {log.error_message}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

const CronManager = () => {
  const theme = useTheme();
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [running, setRunning]     = useState(false);
  const [saving, setSaving]       = useState(false);

  // Form état local
  const [hour, setHour]     = useState('8');
  const [minute, setMinute] = useState('0');
  const [isActive, setIsActive] = useState(true);

  // Exécution programmée
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledAt, setScheduledAt]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await superAdminApi.cron.getConfig();
      if (res.success) {
        setData(res.data);
        setHour(String(res.data.config?.schedule_hour ?? 8));
        setMinute(String(res.data.config?.schedule_minute ?? 0).padStart(2, '0'));
        setIsActive(!!res.data.config?.is_active);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRunNow = async () => {
    if (!window.confirm('Lancer le cron maintenant ?')) return;
    setRunning(true);
    try {
      const res = await superAdminApi.cron.runNow();
      alert(res.message || 'Cron exécuté');
      load();
    } catch {
      alert('Erreur lors de l\'exécution');
    } finally {
      setRunning(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await superAdminApi.cron.updateConfig({
        schedule_hour: parseInt(hour),
        schedule_minute: parseInt(minute),
        is_active: isActive,
      });
      load();
    } finally {
      setSaving(false);
    }
  };

  const handleScheduleOnce = async () => {
    if (!scheduledAt) return;
    try {
      const res = await superAdminApi.cron.scheduleOnce(scheduledAt);
      alert(res.message);
      setScheduleMode(false);
      setScheduledAt('');
      load();
    } catch {
      alert('Erreur lors de la programmation');
    }
  };

  const handleCancelSchedule = async () => {
    await superAdminApi.cron.cancelSchedule();
    load();
  };

  const config = data?.config;
  const logs   = data?.logs || [];
  const stats  = data?.stats || {};

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <span className="animate-spin w-8 h-8 border-2 rounded-full" style={{ borderColor: theme.primary, borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">

      {/* Header */}
      <div>
        <h1 className="text-xl font-bold" style={{ color: theme.text }}>
          Planification des rappels
        </h1>
        <p className="text-sm mt-1" style={{ color: theme.textMuted }}>
          Gérez l'exécution automatique du cron de rappels de facturation
        </p>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 rounded-xl" style={{ background: theme.bgCard, border: `1px solid ${theme.border}` }}>
          <p className="text-2xl font-bold" style={{ color: theme.text }}>{stats.total_active_reminders || 0}</p>
          <p className="text-xs mt-1" style={{ color: theme.textMuted }}>Rappels actifs au total</p>
        </div>
        <div className="p-4 rounded-xl" style={{ background: theme.bgCard, border: `1px solid ${theme.border}` }}>
          <p className="text-2xl font-bold" style={{ color: theme.text }}>{stats.total_companies_configured || 0}</p>
          <p className="text-xs mt-1" style={{ color: theme.textMuted }}>Entreprises configurées</p>
        </div>
      </div>

      {/* Statut actuel */}
      <div className="p-5 rounded-2xl" style={{ background: theme.bgCard, border: `1px solid ${theme.border}` }}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: isActive ? theme.successSoft : theme.dangerSoft }}>
              <Power size={18} style={{ color: isActive ? theme.successText : theme.dangerText }} />
            </div>
            <div>
              <p className="font-semibold text-sm" style={{ color: theme.text }}>
                Cron {isActive ? 'actif' : 'désactivé'}
              </p>
              <p className="text-xs" style={{ color: theme.textMuted }}>
                {isActive
                  ? `Exécution quotidienne à ${String(config?.schedule_hour ?? 8).padStart(2,'0')}h${String(config?.schedule_minute ?? 0).padStart(2,'0')}`
                  : 'Le cron ne s\'exécutera pas automatiquement'}
              </p>
            </div>
          </div>

          {/* Prochaine exécution */}
          <div className="text-right">
            <p className="text-xs font-semibold" style={{ color: theme.textSecondary }}>Prochaine exécution</p>
            <p className="text-xs mt-0.5" style={{ color: theme.primary }}>
              {config?.scheduled_once_at
                ? `📅 ${formatDateTime(config.scheduled_once_at)}`
                : config?.next_run_at
                  ? formatDateTime(config.next_run_at)
                  : '—'}
            </p>
            {config?.last_run_at && (
              <p className="text-xs mt-1" style={{ color: theme.textMuted }}>
                Dernière : {formatDateTime(config.last_run_at)}
              </p>
            )}
          </div>
        </div>

        {/* Exécution unique programmée */}
        {config?.scheduled_once_at && (
          <div className="mt-4 flex items-center justify-between p-3 rounded-xl"
            style={{ background: theme.primarySofter, border: `1px solid ${theme.primaryBorder}` }}>
            <div className="flex items-center gap-2 text-xs" style={{ color: theme.primary }}>
              <Calendar size={14} />
              <span>Exécution unique programmée : <strong>{formatDateTime(config.scheduled_once_at)}</strong></span>
            </div>
            <button
              onClick={handleCancelSchedule}
              className="text-xs px-3 py-1 rounded-lg flex items-center gap-1"
              style={{ background: theme.dangerSoft, color: theme.dangerText }}
            >
              <Ban size={12} /> Annuler
            </button>
          </div>
        )}
      </div>

      {/* Actions rapides */}
      <div className="grid grid-cols-2 gap-4">

        {/* Exécuter maintenant */}
        <div className="p-5 rounded-2xl space-y-3" style={{ background: theme.bgCard, border: `1px solid ${theme.border}` }}>
          <div className="flex items-center gap-2">
            <Zap size={16} style={{ color: theme.primary }} />
            <p className="font-semibold text-sm" style={{ color: theme.text }}>Exécuter maintenant</p>
          </div>
          <p className="text-xs" style={{ color: theme.textMuted }}>
            Lance immédiatement le cron pour tous les rappels du jour.
          </p>
          <button
            onClick={handleRunNow}
            disabled={running}
            className="w-full py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: theme.primary, color: '#fff' }}
          >
            {running
              ? <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              : <Play size={14} />}
            {running ? 'En cours...' : 'Lancer'}
          </button>
        </div>

        {/* Programmer une date */}
        <div className="p-5 rounded-2xl space-y-3" style={{ background: theme.bgCard, border: `1px solid ${theme.border}` }}>
          <div className="flex items-center gap-2">
            <Calendar size={16} style={{ color: theme.primary }} />
            <p className="font-semibold text-sm" style={{ color: theme.text }}>Programmer une exécution</p>
          </div>
          <p className="text-xs" style={{ color: theme.textMuted }}>
            Planifiez une exécution unique à une date et heure précises.
          </p>
          {!scheduleMode ? (
            <button
              onClick={() => setScheduleMode(true)}
              className="w-full py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
              style={{ background: theme.primarySoft, color: theme.primary }}
            >
              <Calendar size={14} /> Choisir une date
            </button>
          ) : (
            <div className="space-y-2">
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={e => setScheduledAt(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ background: theme.bgInput, border: `1px solid ${theme.border}`, color: theme.text }}
              />
              <div className="flex gap-2">
                <button
                  onClick={() => { setScheduleMode(false); setScheduledAt(''); }}
                  className="flex-1 py-2 rounded-lg text-xs"
                  style={{ background: theme.bgInput, color: theme.textMuted }}
                >
                  Annuler
                </button>
                <button
                  onClick={handleScheduleOnce}
                  disabled={!scheduledAt}
                  className="flex-1 py-2 rounded-lg text-xs font-semibold disabled:opacity-50"
                  style={{ background: theme.primary, color: '#fff' }}
                >
                  Confirmer
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Configuration heure quotidienne */}
      <div className="p-5 rounded-2xl space-y-4" style={{ background: theme.bgCard, border: `1px solid ${theme.border}` }}>
        <p className="font-semibold text-sm" style={{ color: theme.text }}>
          Paramètres d'exécution quotidienne
        </p>

        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-xs" style={{ color: theme.textSecondary }}>Heure</label>
            <select
              value={hour}
              onChange={e => setHour(e.target.value)}
              className="px-3 py-1.5 rounded-lg text-sm"
              style={{ background: theme.bgInput, border: `1px solid ${theme.border}`, color: theme.text }}
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{String(i).padStart(2, '0')}h</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs" style={{ color: theme.textSecondary }}>Minute</label>
            <select
              value={minute}
              onChange={e => setMinute(e.target.value)}
              className="px-3 py-1.5 rounded-lg text-sm"
              style={{ background: theme.bgInput, border: `1px solid ${theme.border}`, color: theme.text }}
            >
              {['00','05','10','15','20','25','30','35','40','45','50','55'].map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsActive(a => !a)}
              className="relative w-11 h-6 rounded-full transition-colors"
              style={{ background: isActive ? theme.primary : theme.border }}
            >
              <span className="absolute top-1 w-4 h-4 rounded-full bg-white transition-all"
                style={{ left: isActive ? '1.375rem' : '0.25rem' }} />
            </button>
            <label className="text-xs" style={{ color: theme.textSecondary }}>
              {isActive ? 'Cron actif' : 'Cron désactivé'}
            </label>
          </div>

          <button
            onClick={handleSaveConfig}
            disabled={saving}
            className="px-4 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
            style={{ background: theme.primary, color: '#fff' }}
          >
            {saving
              ? <span className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
              : null}
            Sauvegarder
          </button>
        </div>

        <div className="flex items-start gap-2 text-xs p-3 rounded-lg"
          style={{ background: theme.bgInput }}>
          <Info size={13} style={{ color: theme.textMuted }} className="mt-0.5 flex-shrink-0" />
          <span style={{ color: theme.textMuted }}>
            Le cron s'exécute chaque jour à l'heure configurée et envoie les notifications aux entreprises
            dont le rappel tombe ce jour-là. Fuseau horaire : Africa/Brazzaville.
          </span>
        </div>
      </div>

      {/* Historique */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <p className="font-semibold text-sm" style={{ color: theme.text }}>
            Historique des exécutions
          </p>
          <button onClick={load} className="p-2 rounded-lg" style={{ color: theme.textMuted }}>
            <RefreshCw size={14} />
          </button>
        </div>

        {logs.length === 0 ? (
          <div className="text-center py-8 rounded-xl" style={{ background: theme.bgCard, border: `1px solid ${theme.border}` }}>
            <Clock size={24} className="mx-auto mb-2" style={{ color: theme.textMuted }} />
            <p className="text-sm" style={{ color: theme.textMuted }}>Aucune exécution enregistrée</p>
          </div>
        ) : (
          logs.map(log => <LogRow key={log.id} log={log} theme={theme} />)
        )}
      </div>
    </div>
  );
};

export default CronManager;